import { z } from 'zod';
import { DSLTermQueryDTO } from '@/shared/api/opensearch/term-query';
import { DSLTermsQueryDTO } from '@/shared/api/opensearch/terms-query';
import { DSLMatchQueryDTO } from '@/shared/api/opensearch/match-query';
import { DSLRangeQueryDTO } from '@/shared/api/opensearch/range-query';
import { DSLMultiMatchQueryDTO } from '@/shared/api/opensearch/multi-match-query';
import {
  DSLMatchAllQuery,
  DSLMatchAllQueryDTO,
} from '@/shared/api/opensearch/match-all-query';
import { DSLPrefixQueryDTO } from '@/shared/api/opensearch/prefix-query';
import { DSLExistsQueryDTO } from '@/shared/api/opensearch/exists-query';

export enum DSLBoolClauseEnum {
  MUST = 'must',
  FILTER = 'filter',
  SHOULD = 'should',
  MUST_NOT = 'must_not',
}

export const DSLBoolClauseEnumDTO = z.enum([
  'must',
  'filter',
  'should',
  'must_not',
]);

export type DSLBoolClause = z.infer<typeof DSLBoolClauseEnumDTO>;

export type DSLLeafQuery =
  | z.infer<typeof DSLTermQueryDTO>
  | z.infer<typeof DSLTermsQueryDTO>
  | z.infer<typeof DSLRangeQueryDTO>
  | z.infer<typeof DSLPrefixQueryDTO>
  | z.infer<typeof DSLMatchQueryDTO>
  | z.infer<typeof DSLMultiMatchQueryDTO>
  | z.infer<typeof DSLExistsQueryDTO>;

export const DSLQueryClauseDTO: z.ZodType<
  DSLLeafQuery | DSLBoolQuery | DSLMatchAllQuery
> = z.lazy(() =>
  z.union([
    DSLTermQueryDTO,
    DSLTermsQueryDTO,
    DSLRangeQueryDTO,
    DSLPrefixQueryDTO,
    DSLMatchQueryDTO,
    DSLMultiMatchQueryDTO,
    DSLMatchAllQueryDTO,
    DSLExistsQueryDTO,
    DSLBoolQueryDTO,
  ]),
);

const DSLBoolGroupsDTO = z.object({
  must: z.array(DSLQueryClauseDTO).min(1).optional(),
  filter: z.array(DSLQueryClauseDTO).min(1).optional(),
  should: z.array(DSLQueryClauseDTO).min(1).optional(),
  must_not: z.array(DSLQueryClauseDTO).min(1).optional(),
});

const DSLBoolInnerDTO = DSLBoolGroupsDTO.extend({
  minimum_should_match: z.union([z.string(), z.number()]).optional(),
  boost: z.number().optional(),
}).refine(
  (value) =>
    DSLBoolClauseEnumDTO.options.some((clause) => value[clause] !== undefined),
  {
    message: 'Bool query must contain at least one clause group',
  },
);

export const DSLBoolQueryDTO: z.ZodType<DSLBoolQuery> = z.looseObject({
  bool: DSLBoolInnerDTO,
});

export type DSLBoolQuery = {
  bool: z.infer<typeof DSLBoolInnerDTO>;
};